// Training overview screens: managers (Courses / People) and staff (what's due, what's done).
// The quiz, question editor and create-course screens stay in training-page.tsx.
import { useEffect, useMemo, useState } from 'react';
import DateInput from '@/src/components/date-input';
import { Plus, Loader2, X, BellRing, RotateCcw, Pencil, Trash2, ChevronDown, Check, FileText } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import { openTaskLink } from '@/src/lib/taskLinks';
import { modeOf, type TrainingAssignment, type TrainingCourse } from '@/src/hooks/useTraining';

type Member = { id: string; name: string; app_role: string };
type CourseInfo = { questions: number; docs: Array<{ bucket: string; name: string }> };
const PASS = 70;

const docName = (n: string) => n.replace(/^\d{10,}-/, '').replace(/\.[a-z0-9]+$/i, '').replace(/_/g, ' ');
const initials = (n: string) => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
const dueLabel = (iso: string) => {
  const d = new Date(iso); const now = new Date();
  const days = Math.round((new Date(d.toDateString()).getTime() - new Date(now.toDateString()).getTime()) / 86400000);
  if (days < -1) return `${-days} days overdue`;
  if (days === -1) return 'Due yesterday';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days < 7) return `Due ${d.toLocaleDateString('en-AU', { weekday: 'long' })}`;
  return `Due ${d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
};
const isOverdue = (a: TrainingAssignment) => !!a.due_at && !a.completed_at && new Date(new Date(a.due_at).toDateString()) < new Date(new Date().toDateString());
const passed = (a?: TrainingAssignment) => !!a?.completed_at && (a.score ?? 0) >= PASS;
const failed = (a?: TrainingAssignment) => !!a?.completed_at && (a.score ?? 0) < PASS;
/** A test that's been opened or finished: staff can't take it again until a manager reopens it. */
const testUsed = (course: TrainingCourse, a?: TrainingAssignment) => modeOf(course) === 'test' && !!(a?.completed_at || a?.started_at);

/** Reopen a quiz for one person (clears the result and, for tests, the start). */
async function reopenAssignment(id: string) {
  let { error } = await supabase.from('training_assignments').update({ score: null, completed_at: null, started_at: null }).eq('id', id);
  if (error && /started_at|schema cache|column/i.test(error.message)) ({ error } = await supabase.from('training_assignments').update({ score: null, completed_at: null }).eq('id', id));
  return error;
}

function useCourseInfo(venueId: string, courses: TrainingCourse[]) {
  const [info, setInfo] = useState<Record<string, CourseInfo>>({});
  useEffect(() => {
    if (!courses.length) return;
    let off = false;
    (async () => {
      let r = await supabase.from('training_modules').select('course_id, quiz, source_docs').in('course_id', courses.map((c) => c.id));
      if (r.error) r = await supabase.from('training_modules').select('course_id, quiz').in('course_id', courses.map((c) => c.id)) as typeof r;
      if (off) return;
      const m: Record<string, CourseInfo> = {};
      for (const row of (r.data ?? []) as Array<{ course_id: string; quiz: unknown[] | null; source_docs?: Array<{ bucket: string; name: string }> | null }>) {
        const cur = (m[row.course_id] ??= { questions: 0, docs: [] });
        cur.questions += Array.isArray(row.quiz) ? row.quiz.length : 0;
        for (const d of row.source_docs ?? []) if (!cur.docs.some((x) => x.name === d.name)) cur.docs.push(d);
      }
      setInfo(m);
    })();
    return () => { off = true; };
  }, [venueId, courses]);
  return info;
}

function Avatar({ name, size = 36 }: { name: string; size?: number }) {
  const c = useAppColors();
  return <span className="rounded-full flex items-center justify-center font-bold shrink-0" aria-hidden="true" style={{ width: size, height: size, fontSize: Math.round(size * 0.33), background: c.accent, color: '#fff' }}>{initials(name)}</span>;
}

function Pill({ tone, children }: { tone: 'green' | 'red' | 'amber' | 'grey' | 'redLine'; children: React.ReactNode }) {
  const c = useAppColors();
  const d = c.isDark;
  const s = {
    green: { color: d ? '#A7E3A7' : '#1F3F27', background: c.accentBg },
    red: { color: d ? '#FCA5A5' : '#9A2A1C', background: d ? 'rgba(248,113,113,0.14)' : '#FCE8E4' },
    redLine: { color: d ? '#FCA5A5' : '#B42318', background: 'transparent', boxShadow: `inset 0 0 0 1.5px ${d ? 'rgba(248,113,113,0.5)' : '#E7B3AA'}` },
    amber: { color: d ? '#FCD38D' : '#8A4B08', background: d ? 'rgba(232,160,32,0.16)' : '#FCEFD9' },
    grey: { color: c.text, background: c.iconBg },
  }[tone];
  return <span className="inline-flex items-center text-[12px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap" style={s}>{children}</span>;
}

/* =================== Managers =================== */

export function ManagerTraining({ venueId, courses, allAssignments, members, reload, assignCourse, onCreate, onOpen, onEdit, onDelete, lastScore }: {
  venueId: string; courses: TrainingCourse[]; allAssignments: TrainingAssignment[]; members: Member[];
  reload: () => Promise<unknown>;
  assignCourse: (courseId: string, userId: string, dueAt?: string) => Promise<{ error: string | null }>;
  onCreate: () => void; onOpen: (c: TrainingCourse) => void; onEdit: (c: TrainingCourse) => void; onDelete: (c: TrainingCourse) => void;
  lastScore: number | null;
}) {
  const c = useAppColors();
  const [tab, setTab] = useState<'courses' | 'people'>(() => { try { return (localStorage.getItem('opsalo:training-tab') as 'courses' | 'people') || 'courses'; } catch { return 'courses'; } });
  const pick = (t: 'courses' | 'people') => { setTab(t); try { localStorage.setItem('opsalo:training-tab', t); } catch { /* ignore */ } };
  const info = useCourseInfo(venueId, courses);
  const [assignFor, setAssignFor] = useState<TrainingCourse | null>(null);
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const nameOf = (id: string) => members.find((m) => m.id === id)?.name ?? 'Team member';
  const courseIds = new Set(courses.map((x) => x.id));
  const live = allAssignments.filter((a) => courseIds.has(a.course_id) && members.some((m) => m.id === a.user_id));

  const done = live.filter((a) => passed(a)).length;
  const overdue = live.filter(isOverdue);
  const scored = live.filter((a) => a.completed_at && a.score != null);
  const avg = scored.length ? Math.round(scored.reduce((s, a) => s + (a.score ?? 0), 0) / scored.length) : null;

  const attention = [
    ...overdue.map((a) => ({ a, kind: 'overdue' as const })),
    ...live.filter(failed).map((a) => ({ a, kind: 'failed' as const })),
  ].slice(0, 6);

  const remind = async (userIds: string[], courseId: string) => {
    const { data, error } = await supabase.rpc('remind_training', { p_user_ids: userIds, p_course_id: courseId });
    if (error) { setMsg({ text: /function|does not exist|schema cache/i.test(error.message) ? 'Reminders need the latest database update (20261023060000_training_reminders.sql).' : error.message, bad: true }); return; }
    const n = Number(data ?? 0);
    setMsg({ text: n ? `Reminder sent to ${n} ${n === 1 ? 'person' : 'people'}.` : 'Already reminded in the last 2 hours.' });
  };
  const retake = async (a: TrainingAssignment) => {
    const error = await reopenAssignment(a.id);
    if (error) { setMsg({ text: error.message, bad: true }); return; }
    await reload();
    await remind([a.user_id], a.course_id);
  };
  const remindAllOverdue = async () => {
    const byCourse = new Map<string, string[]>();
    for (const a of overdue) byCourse.set(a.course_id, [...(byCourse.get(a.course_id) ?? []), a.user_id]);
    for (const [cid, ids] of byCourse) await remind(ids, cid);
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold tracking-tight" style={{ color: c.text }}>Training</h1>
          <p className="text-sm" style={{ color: c.textDim }}>Courses and quizzes for your team, made from your own documents.</p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <div role="tablist" className="flex p-1 rounded-xl" style={{ background: c.iconBg }}>
            {(['courses', 'people'] as const).map((t) => (
              <button key={t} role="tab" aria-selected={tab === t} onClick={() => pick(t)} className="h-9 px-3.5 rounded-lg text-sm font-semibold capitalize"
                style={tab === t ? { background: c.bgElevated, color: c.text, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' } : { color: c.textDim }}>{t}</button>
            ))}
          </div>
          {tab === 'people' && overdue.length > 0 && (
            <button onClick={() => void remindAllOverdue()} className="h-11 px-3.5 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
              <BellRing size={15} /> Remind everyone overdue
            </button>
          )}
          <button onClick={onCreate} className="h-11 px-4 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.accent, color: '#fff' }}><Plus size={16} /> New course</button>
        </div>
      </header>
      {msg && <p className="text-sm" style={{ color: msg.bad ? c.danger : c.accentSoft }}>{msg.text}</p>}
      {lastScore !== null && <p className="text-sm" style={{ color: lastScore >= PASS ? c.accentSoft : c.danger }}>{lastScore >= PASS ? `You scored ${lastScore}% and passed.` : `You scored ${lastScore}%. ${PASS}% passes.`}</p>}

      {tab === 'courses' ? (
        <>
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat label="Team done" value={live.length ? `${Math.round((done / live.length) * 100)}%` : '—'} sub={`${done} of ${live.length} assignments`} />
            <Stat label="Overdue" value={String(overdue.length)} sub={overdue.length ? `${new Set(overdue.map((a) => a.user_id)).size} ${new Set(overdue.map((a) => a.user_id)).size === 1 ? 'person' : 'people'}` : 'All on time'} bad={overdue.length > 0} />
            <Stat label="Average score" value={avg != null ? `${avg}%` : '—'} sub={`Pass mark ${PASS}%`} />
            <Stat label="Courses" value={String(courses.length)} sub={`${courses.filter((x) => !live.some((a) => a.course_id === x.id)).length} not assigned yet`} />
          </section>

          {attention.length > 0 && (
            <section className="rounded-2xl px-5 py-4" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
              <h2 className="text-base font-bold mb-1.5" style={{ color: c.text }}>Needs attention</h2>
              {attention.map(({ a, kind }, i) => {
                const course = courses.find((x) => x.id === a.course_id);
                const first = nameOf(a.user_id).split(' ')[0];
                return (
                  <div key={a.id + kind} className="flex items-center gap-3.5 py-2.5 flex-wrap sm:flex-nowrap" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                    <Avatar name={nameOf(a.user_id)} />
                    <div className="flex-1 min-w-[160px]">
                      <p className="text-sm" style={{ color: c.text }}>
                        <b>{first}</b> {kind === 'overdue' ? (a.attempts ? "hasn't passed" : "hasn't started") : `scored ${a.score}% on`} <b>{course?.title}</b>
                      </p>
                      <p className="text-[13px]" style={{ color: kind === 'overdue' ? c.danger : c.textDim }}>
                        {kind === 'overdue' && a.due_at ? dueLabel(a.due_at) : `Needs ${PASS}% to pass${a.attempts > 1 ? ` · ${a.attempts} tries` : ''}`}
                      </p>
                    </div>
                    {kind === 'failed' ? (
                      <button onClick={() => void retake(a)} className="h-9 px-3 rounded-lg text-[13px] font-semibold" style={{ background: c.accentBg, color: c.accentSoft }}>{course && modeOf(course) === 'test' ? 'Reopen test' : 'Ask to retake'}</button>
                    ) : (
                      <button onClick={() => void remind([a.user_id], a.course_id)} className="h-9 px-3 rounded-lg text-[13px] font-semibold" style={{ background: c.accentBg, color: c.accentSoft }}>Remind</button>
                    )}
                  </div>
                );
              })}
            </section>
          )}

          <section className="grid gap-3.5 md:grid-cols-2">
            {courses.map((course) => {
              const as = live.filter((a) => a.course_id === course.id);
              const p = as.filter(passed).length, f = as.filter(failed).length, o = as.filter(isOverdue).length;
              const sc = as.filter((a) => a.completed_at && a.score != null);
              const cAvg = sc.length ? Math.round(sc.reduce((s, a) => s + (a.score ?? 0), 0) / sc.length) : null;
              const nextDue = as.filter((a) => a.due_at && !a.completed_at).map((a) => a.due_at as string).sort()[0];
              const ci = info[course.id];
              const expanded = open === course.id;
              return (
                <article key={course.id} className="rounded-2xl p-5 flex flex-col gap-3.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 space-y-1">
                      <p className="text-[12px] font-bold flex items-center gap-2" style={{ color: c.accentSoft }}>
                        {course.category && <span>{course.category}</span>}
                        <span className="px-1.5 py-0.5 rounded-md text-[10px] uppercase tracking-wider" style={modeOf(course) === 'test' ? { background: 'rgba(232,160,32,0.14)', color: '#E8A020' } : { background: c.iconBg, color: c.textDim }}>
                          {modeOf(course) === 'test' ? 'Test' : 'Learning'}
                        </span>
                      </p>
                      <h3 className="text-lg font-bold leading-snug"><button onClick={() => onOpen(course)} title="Preview the quiz" className="text-left hover:underline" style={{ color: c.text }}>{course.title}</button></h3>
                      <p className="text-[13px]" style={{ color: c.textDim }}>
                        {[ci?.questions ? `${ci.questions} questions` : null, ci?.docs.length ? `from ${ci.docs.map((d) => docName(d.name)).join(', ')}` : null].filter(Boolean).join(' · ') || 'Quiz'}
                      </p>
                    </div>
                    {as.length === 0 ? <Pill tone="grey">Not assigned</Pill>
                      : o ? <Pill tone="red">{o} overdue</Pill>
                      : p === as.length ? <Pill tone="green">Everyone passed</Pill>
                      : nextDue ? <Pill tone="amber">{dueLabel(nextDue)}</Pill> : null}
                  </div>
                  {as.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex justify-between text-[13px]"><span className="font-semibold" style={{ color: c.text }}>{p} of {as.length} passed</span><span style={{ color: c.textDim }}>{cAvg != null ? `Avg ${cAvg}%` : 'No scores yet'}</span></div>
                      <div className="h-2 rounded-full overflow-hidden flex" style={{ background: c.iconBg }} role="img" aria-label={`${p} passed, ${f} didn't pass, ${as.length - p - f} to go`}>
                        <span style={{ width: `${(p / as.length) * 100}%`, background: c.accent }} />
                        <span style={{ width: `${(f / as.length) * 100}%`, background: c.isDark ? 'rgba(248,113,113,0.6)' : '#F2B8AE' }} />
                      </div>
                    </div>
                  )}
                  {expanded && as.length > 0 && (
                    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${c.border}` }}>
                      {as.map((a, i) => (
                        <div key={a.id} className="flex items-center gap-3 px-3 py-2" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                          <Avatar name={nameOf(a.user_id)} size={28} />
                          <span className="flex-1 text-sm truncate" style={{ color: c.text }}>{nameOf(a.user_id)}</span>
                          {a.completed_at ? <Pill tone={passed(a) ? 'green' : 'red'}>{a.score ?? 0}%</Pill> : a.started_at && modeOf(course) === 'test' ? <Pill tone="amber">Started</Pill> : isOverdue(a) ? <Pill tone="redLine">Overdue</Pill> : <Pill tone="grey">To do</Pill>}
                          {testUsed(course, a) && (
                            <button onClick={() => { if (window.confirm(`Reopen "${course.title}" for ${nameOf(a.user_id)}? Their result is cleared and they can take it once more.`)) void retake(a); }}
                              className="h-7 px-2 rounded-lg text-[11px] font-semibold flex items-center gap-1" style={{ color: c.accentSoft }}>
                              <RotateCcw size={11} /> Reopen
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 mt-auto items-center">
                    <button onClick={() => onEdit(course)} className="h-10 px-3.5 rounded-xl text-[13px] font-semibold flex items-center gap-1.5" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}><Pencil size={13} /> Edit questions</button>
                    <button onClick={() => setAssignFor(course)} className="h-10 px-3.5 rounded-xl text-[13px] font-semibold" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}>Assign</button>
                    <span className="flex-1" />
                    {as.length > 0 && (
                      <button onClick={() => setOpen(expanded ? null : course.id)} className="h-10 px-2.5 rounded-xl text-[13px] font-semibold flex items-center gap-1" style={{ color: c.accentSoft }}>
                        Results <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : undefined }} />
                      </button>
                    )}
                    <button onClick={() => onDelete(course)} aria-label={`Delete ${course.title}`} className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ color: c.textDim }}><Trash2 size={15} /></button>
                  </div>
                </article>
              );
            })}
            <article className="rounded-2xl p-5 flex flex-col gap-2.5 justify-center" style={{ border: `2px dashed ${c.borderStrong}` }}>
              <h3 className="text-[17px] font-bold" style={{ color: c.text }}>Make a course from a document</h3>
              <p className="text-sm" style={{ color: c.textDim }}>Pick a menu, recipe or SOP. OpsAlo writes the questions, you check them, then assign.</p>
              <button onClick={onCreate} className="self-start h-10 px-3.5 rounded-xl text-[13px] font-semibold" style={{ background: c.accent, color: '#fff' }}>New course</button>
            </article>
          </section>
        </>
      ) : (
        <PeopleGrid courses={courses} live={live} members={members} reload={reload} />
      )}

      {assignFor && (
        <AssignModal course={assignFor} members={members} assigned={new Set(live.filter((a) => a.course_id === assignFor.id).map((a) => a.user_id))}
          onClose={() => setAssignFor(null)}
          onAssign={async (ids, due) => {
            for (const id of ids) { const r = await assignCourse(assignFor.id, id, due ?? undefined); if (r.error) return r.error; }
            await reload();
            setMsg({ text: `${assignFor.title} assigned to ${ids.length} ${ids.length === 1 ? 'person' : 'people'}.` });
            setAssignFor(null);
            return null;
          }} />
      )}
    </div>
  );
}

function Stat({ label, value, sub, bad }: { label: string; value: string; sub: string; bad?: boolean }) {
  const c = useAppColors();
  return (
    <div className="rounded-2xl p-4" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
      <p className="text-[13px]" style={{ color: c.textDim }}>{label}</p>
      <p className="text-[26px] font-bold leading-tight" style={{ color: bad ? c.danger : c.text }}>{value}</p>
      <p className="text-[12px] truncate" style={{ color: c.textDim }}>{sub}</p>
    </div>
  );
}

function PeopleGrid({ courses, live, members, reload }: { courses: TrainingCourse[]; live: TrainingAssignment[]; members: Member[]; reload: () => Promise<unknown> }) {
  const c = useAppColors();
  const people = useMemo(() => [...members].sort((a, b) => a.name.localeCompare(b.name)), [members]);
  const resetPerson = async (m: Member) => {
    if (!window.confirm(`Reset all training results for ${m.name}? They'll need to take every quiz again.`)) return;
    await supabase.from('training_assignments').update({ score: null, completed_at: null, attempts: 0 }).eq('user_id', m.id).in('course_id', courses.map((x) => x.id));
    await reload();
  };
  if (!people.length || !courses.length) {
    return <div className="rounded-2xl p-10 text-center text-sm" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.textDim }}>{courses.length ? 'No one on the team can take training yet.' : 'Make a course first.'}</div>;
  }
  return (
    <div className="space-y-3">
      <div className="rounded-2xl overflow-x-auto" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
        <table className="w-full border-collapse" style={{ minWidth: 220 + courses.length * 130 }}>
          <thead>
            <tr className="text-[12px]" style={{ color: c.textDim }}>
              <th scope="col" className="text-left font-semibold px-4 py-3.5 sticky left-0" style={{ background: c.bgElevated }}>Person</th>
              {courses.map((x) => <th key={x.id} scope="col" className="font-semibold px-2.5 py-3.5 text-center">{x.title}</th>)}
              <th scope="col" className="font-semibold px-4 py-3.5 text-right">Passed</th>
            </tr>
          </thead>
          <tbody>
            {people.map((m) => {
              const mine = live.filter((a) => a.user_id === m.id);
              return (
                <tr key={m.id} style={{ borderTop: `1px solid ${c.border}` }}>
                  <td className="px-4 py-3 sticky left-0" style={{ background: c.bgElevated }}>
                    <div className="flex items-center gap-3">
                      <Avatar name={m.name} size={32} />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: c.text }}>{m.name}</p>
                        <p className="text-[12px] capitalize" style={{ color: c.textDim }}>{m.app_role}</p>
                      </div>
                    </div>
                  </td>
                  {courses.map((x) => {
                    const a = mine.find((y) => y.course_id === x.id);
                    return (
                      <td key={x.id} className="px-2.5 py-3 text-center">
                        {!a ? <span style={{ color: c.textFaint }} aria-label="Not assigned">—</span>
                          : a.completed_at ? <Pill tone={passed(a) ? 'green' : 'red'}>{a.score ?? 0}%</Pill>
                          : isOverdue(a) ? <Pill tone="redLine">Overdue</Pill>
                          : a.due_at ? <Pill tone="amber">{dueLabel(a.due_at).replace('Due ', 'Due ')}</Pill>
                          : <Pill tone="grey">Not started</Pill>}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-[13px] font-semibold" style={{ color: c.text }}>{mine.filter(passed).length} of {mine.length}</span>
                    {mine.some((a) => a.completed_at) && (
                      <button onClick={() => void resetPerson(m)} aria-label={`Reset results for ${m.name}`} title="Reset results" className="ml-2 p-1 rounded-md align-middle" style={{ color: c.textDim }}><RotateCcw size={13} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-4 flex-wrap text-[13px]" style={{ color: c.textDim }}>
        <span className="flex items-center gap-1.5"><Pill tone="green">90%</Pill> Passed ({PASS}%+)</span>
        <span className="flex items-center gap-1.5"><Pill tone="red">55%</Pill> Didn't pass</span>
        <span className="flex items-center gap-1.5"><Pill tone="grey">Not started</Pill></span>
        <span className="flex items-center gap-1.5"><b style={{ color: c.textFaint }}>—</b> Not assigned</span>
      </div>
    </div>
  );
}

function AssignModal({ course, members, assigned, onClose, onAssign }: {
  course: TrainingCourse; members: Member[]; assigned: Set<string>; onClose: () => void;
  onAssign: (ids: string[], due: string | null) => Promise<string | null>;
}) {
  const c = useAppColors();
  const [ids, setIds] = useState<string[]>([]);
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const free = members.filter((m) => !assigned.has(m.id));
  return (
    <>
      <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60]" onClick={() => !busy && onClose()} />
      <div role="dialog" aria-label={`Assign ${course.title}`} className="modal-panel fixed inset-x-4 lg:inset-x-auto lg:left-1/2 lg:-translate-x-1/2 lg:w-[480px] top-16 rounded-2xl z-[60] overflow-hidden" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
        <header className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${c.border}` }}>
          <h2 className="text-base font-bold truncate" style={{ color: c.text }}>Assign {course.title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-2 -m-1" style={{ color: c.textDim }}><X size={18} /></button>
        </header>
        <div className="px-6 py-5 space-y-4">
          {free.length === 0 ? <p className="text-sm" style={{ color: c.textDim }}>Everyone already has this course.</p> : (
            <>
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold" style={{ color: c.text }}>Who</p>
                <button onClick={() => setIds(ids.length === free.length ? [] : free.map((m) => m.id))} className="text-[13px] font-semibold" style={{ color: c.accentSoft }}>{ids.length === free.length ? 'Clear' : 'Everyone'}</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {free.map((m) => {
                  const on = ids.includes(m.id);
                  return (
                    <button key={m.id} aria-pressed={on} onClick={() => setIds((x) => (on ? x.filter((y) => y !== m.id) : [...x, m.id]))}
                      className="h-10 px-3 rounded-full text-sm flex items-center gap-1.5"
                      style={on ? { background: c.accentBg, color: c.accentSoft, border: `2px solid ${c.accent}`, fontWeight: 700 } : { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}`, fontWeight: 500 }}>
                      {on && <Check size={14} strokeWidth={3} />}{m.name}
                    </button>
                  );
                })}
              </div>
              <label className="block">
                <span className="block text-sm font-semibold mb-1.5" style={{ color: c.text }}>Due by (optional)</span>
                <DateInput value={due} onChange={setDue} clearable ariaLabel="Due date" className="flex h-11 px-3 rounded-xl text-sm min-w-[150px]" style={{ background: c.iconBg, border: `1px solid ${c.border}`, color: c.text }} />
              </label>
            </>
          )}
          {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}
        </div>
        <div className="px-6 py-4" style={{ borderTop: `1px solid ${c.border}` }}>
          <button disabled={!ids.length || busy} onClick={async () => { setBusy(true); const e = await onAssign(ids, due ? new Date(`${due}T23:00:00`).toISOString() : null); setBusy(false); if (e) setErr(e); }}
            className="w-full h-12 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40" style={{ background: c.accent, color: '#fff' }}>
            {busy && <Loader2 size={15} className="animate-spin" />} Assign to {ids.length || ''} {ids.length === 1 ? 'person' : 'people'}
          </button>
        </div>
      </div>
    </>
  );
}

/* =================== Staff =================== */

export function StaffTraining({ venueId, courses, assignments, onStart, lastScore, lastWasTest }: {
  venueId: string; courses: TrainingCourse[]; assignments: TrainingAssignment[];
  onStart: (c: TrainingCourse, review: boolean) => void; lastScore: number | null; lastWasTest?: boolean;
}) {
  const c = useAppColors();
  const info = useCourseInfo(venueId, courses);
  const [err, setErr] = useState<string | null>(null);
  const rows = courses.map((course) => ({ course, a: assignments.find((x) => x.course_id === course.id) }));
  // A finished test is done either way (one attempt). A started-but-unfinished test stays in "to do" but locked.
  const isDone = (r: { course: TrainingCourse; a?: TrainingAssignment }) => passed(r.a) || (modeOf(r.course) === 'test' && !!r.a?.completed_at);
  const todo = rows.filter((r) => !isDone(r)).sort((x, y) => (x.a?.due_at ?? '9999').localeCompare(y.a?.due_at ?? '9999'));
  const done = rows.filter(isDone);
  const [hero, ...rest] = todo;

  const readFirst = async (d: { bucket: string; name: string }) => {
    const e = await openTaskLink({ type: 'document', bucket: d.bucket, name: d.name, label: docName(d.name) } as never, venueId);
    if (e) setErr(e);
  };

  return (
    <div className="space-y-5 max-w-xl mx-auto">
      <div>
        <h1 className="text-[28px] font-bold tracking-tight" style={{ color: c.text }}>Your training</h1>
        <p className="text-sm" style={{ color: c.textDim }}>{courses.length ? `${todo.length} to do · ${done.length} done` : 'Nothing assigned yet. Your manager will add courses for you.'}</p>
      </div>
      {lastScore !== null && <p className="text-sm font-semibold" style={{ color: lastScore >= PASS ? c.accentSoft : c.danger }}>{lastScore >= PASS ? `Nice. You scored ${lastScore}% and passed.` : lastWasTest ? `You scored ${lastScore}%. You needed ${PASS}% to pass. Your manager can see your result.` : `You scored ${lastScore}%. You need ${PASS}% to pass. Have another go when you're ready.`}</p>}
      {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}

      {hero && (() => {
        const ci = info[hero.course.id];
        const tried = failed(hero.a);
        const isTest = modeOf(hero.course) === 'test';
        const locked = testUsed(hero.course, hero.a);
        return (
          <section className="rounded-2xl p-5 space-y-3" style={{ background: c.bgElevated, border: `2px solid ${c.accent}` }}>
            <div className="flex justify-between items-center gap-2 flex-wrap">
              {hero.a?.due_at ? <Pill tone={isOverdue(hero.a) || dueLabel(hero.a.due_at) === 'Due today' ? 'red' : 'amber'}>{dueLabel(hero.a.due_at)}</Pill> : <Pill tone="grey">Up next</Pill>}
              {ci?.questions ? <span className="text-[13px]" style={{ color: c.textDim }}>{ci.questions} questions · about {Math.max(2, Math.round(ci.questions / 2))} min</span> : null}
            </div>
            <h2 className="text-[22px] font-bold leading-tight" style={{ color: c.text }}>{hero.course.title}</h2>
            <p className="text-sm leading-relaxed" style={{ color: c.textDim }}>
              {locked ? 'You started this test but didn\'t finish. Ask your manager to reopen it.'
                : isTest ? `${ci?.docs.length ? `Read the ${docName(ci.docs[0].name)} first. ` : ''}This is a test: one attempt, ${PASS}% to pass.`
                : tried ? `Last try: ${hero.a?.score}%. You need ${PASS}% to pass.` : ci?.docs.length ? `Read the ${docName(ci.docs[0].name)} first. You need ${PASS}% to pass and can retake it.` : `You need ${PASS}% to pass and can retake it.`}
            </p>
            <div className="flex gap-2">
              {!locked && <button onClick={() => onStart(hero.course, false)} className="flex-1 h-[52px] rounded-xl text-base font-bold" style={{ background: c.accent, color: '#fff' }}>{isTest ? 'Start test' : tried ? 'Try again' : 'Start quiz'}</button>}
              {ci?.docs[0] && (
                <button onClick={() => void readFirst(ci.docs[0])} className="h-[52px] px-4 rounded-xl text-sm font-semibold flex items-center gap-1.5" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}><FileText size={15} /> Read first</button>
              )}
            </div>
          </section>
        );
      })()}

      {rest.map(({ course, a }) => (
        <section key={course.id} className="rounded-2xl p-4 flex items-center gap-3.5" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
          <div className="flex-1 min-w-0">
            <p className="text-base font-bold truncate" style={{ color: c.text }}>{course.title}</p>
            <p className="text-[13px] font-semibold" style={{ color: a && isOverdue(a) ? c.danger : c.textDim }}>
              {[modeOf(course) === 'test' ? (testUsed(course, a) ? 'Test started · ask your manager to reopen' : 'Test · one attempt') : null, a?.due_at ? dueLabel(a.due_at) : null, failed(a) ? `Last try ${a?.score}%` : null, info[course.id]?.questions ? `${info[course.id].questions} questions` : null].filter(Boolean).join(' · ') || 'To do'}
            </p>
          </div>
          {!testUsed(course, a) && <button onClick={() => onStart(course, false)} className="h-10 px-4 rounded-xl text-sm font-semibold" style={{ border: `1px solid ${c.borderStrong}`, color: c.text }}>{failed(a) ? 'Retry' : 'Start'}</button>}
        </section>
      ))}

      {done.length > 0 && (
        <div className="space-y-2">
          <p className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.textDim }}>Done</p>
          <div className="rounded-2xl overflow-hidden" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
            {done.map(({ course, a }, i) => (
              <button key={course.id} onClick={() => onStart(course, true)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left" style={{ borderTop: i ? `1px solid ${c.border}` : undefined }}>
                <span className="flex-1 text-[15px] font-semibold truncate" style={{ color: c.text }}>{course.title}</span>
                <Pill tone={passed(a) ? 'green' : 'red'}>{a?.score ?? 0}%</Pill>
              </button>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'documents' } }))}
        className="w-full h-12 rounded-xl text-sm font-semibold" style={{ background: c.bgElevated, border: `1px solid ${c.border}`, color: c.text }}>
        Browse the venue's documents
      </button>
    </div>
  );
}
